"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const ethers_1 = require("ethers");
const did_resolver_interface_1 = require("@ew-did-registry/did-resolver-interface");
const did_1 = require("@ew-did-registry/did");
const constants_1 = require("../constants");
const functions_1 = require("../functions");
const __1 = require("..");
const { formatBytes32String } = ethers_1.utils;
/**
 * To support different methods compliant with ERC1056, the user/developer simply has to provide
 * different resolver settings. The default resolver settings are provided in the 'constants' folder
 * Any settings that follow the IResolverSettings interface are valid.
 *
 * The read functionality is implemented in Resolver class. If one wants to adjust it or create her
 * own implementation (for example according to ERC725), one could use this class as a
 * starting point.
 * All the functionality supporting document resolution is stored in 'functions' folder.
 */
class Resolver {
    /**
     * Constructor
     *
     * @param settings - Settings to connect to Ethr registry
     * @param provider - Ethers provider. Can be obtained from getProvider(providerSettings)
     */
    constructor(provider, settings) {
        this._provider = provider;
        this.settings = Object.assign({ abi: constants_1.ethrReg.abi, method: did_1.Methods.Erc1056 }, settings);
        this._contract = new ethers_1.Contract(settings.address, this.settings.abi, this._provider);
    }
    /**
     * Resolve DID Document for a given did
     *
     * @example
     * ```typescript
     * import { Resolver } from '@ew-did-registry/did-resolver';
     *
     * const resolver = new Resolver(provider, resolverSettings);
     * const didDocument = await resolver.read(did);
     * ```
     *
     * @param did - entity identifier, which is associated with DID Document
     * @returns {Promise<IDIDDocument>}
     */
    _read(did, selector) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = (0, __1.matchDIDPattern)(did);
            const address = match[1];
            const _document = {
                owner: address,
                topBlock: ethers_1.BigNumber.from(0),
                authentication: {},
                publicKey: {},
                service: {},
                attributes: new Map(),
            };
            try {
                yield (0, functions_1.fetchDataFromEvents)(did, _document, this.settings, this._contract, this._provider, selector);
                const document = (0, functions_1.wrapDidDocument)(did, _document);
                return document;
            }
            catch (error) {
                if (error instanceof Error &&
                    error.toString() ===
                        'Error: Blockchain address did not interact with smart contract') {
                    const didDocument = (0, functions_1.wrapDidDocument)(did, _document);
                    return didDocument;
                }
                throw error;
            }
        });
    }
    read(did) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._read(did);
        });
    }
    readAttribute(did, selector) {
        return __awaiter(this, void 0, void 0, function* () {
            const doc = yield this._read(did, selector);
            return (0, functions_1.query)(doc, selector);
        });
    }
    /**
     * Returns the Ethereum address of current identity owner
     *
     * @param { string } did - did of identity of interest
     * @returns Promise<string>
     */
    identityOwner(did) {
        return __awaiter(this, void 0, void 0, function* () {
            const id = (0, __1.addressOf)(did);
            let owner;
            try {
                owner = yield this._contract.identityOwner(id);
            }
            catch (error) {
                if (error instanceof Error) {
                    throw new Error(error.message);
                }
            }
            return owner;
        });
    }
    /**
     * Performs the check if the delegate is valid for particular did
     * Return boolean
     *
     * @param { string } identityDID - did of identity of interest
     * @param { DelegateTypes } delegateType - type of delegate of interest
     * @param { delegateDID } did - did of delegate of interest
     * @returns Promise<boolean>
     */
    validDelegate(identityDID, delegateType, delegateDID) {
        return __awaiter(this, void 0, void 0, function* () {
            const bytesType = formatBytes32String(delegateType);
            const identityAddress = (0, __1.addressOf)(identityDID);
            const delegateAddress = (0, __1.addressOf)(delegateDID);
            let valid;
            try {
                valid = yield this._contract.validDelegate(identityAddress, bytesType, delegateAddress);
            }
            catch (error) {
                if (error instanceof Error) {
                    throw new Error(error.message);
                }
            }
            return valid;
        });
    }
    readOwnerPubKey(did) {
        return __awaiter(this, void 0, void 0, function* () {
            const selector = {
                publicKey: { id: `${did}#${did_resolver_interface_1.KeyTags.OWNER}` },
            };
            const pk = yield this.readAttribute(did, selector);
            const publicKeyHex = pk
                ? pk.publicKeyHex
                : undefined;
            if (!publicKeyHex) {
                return undefined;
            }
            if (publicKeyHex.length === __1.compressedSecp256k1KeyLength + 2 &&
                publicKeyHex.substring(0, 2) === '0x') {
                return publicKeyHex.substring(2);
            }
            if (publicKeyHex.length === __1.compressedSecp256k1KeyLength) {
                return publicKeyHex;
            }
            return undefined;
        });
    }
    readFromBlock(did, topBlock) {
        return __awaiter(this, void 0, void 0, function* () {
            const address = (0, __1.addressOf)(did);
            const _document = {
                owner: address,
                topBlock,
                authentication: {},
                publicKey: {},
                service: {},
                attributes: new Map(),
            };
            yield (0, functions_1.fetchDataFromEvents)(did, _document, this.settings, this._contract, this._provider);
            return Object.assign({}, _document);
        });
    }
    lastBlock(did) {
        return __awaiter(this, void 0, void 0, function* () {
            const address = (0, __1.addressOf)(did);
            return this._contract.changed(address);
        });
    }
}
exports.default = Resolver;
//# sourceMappingURL=resolver.js.map